import React from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { IAppContext } from '@kurtosys/types/helpers/AnalyticsHelper/IAppContext.js';
import { makeObservable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase, InjectedStyledComponent } from '../../../../../shared/index.js';
import ContextMenu from '../../../ContextMenu/ContextMenu.js';
import Icon from '../../../Icon/Icon.js';
import { ITranslationContextValue } from '../../../Translate/models/ITranslationContextValue.js';
import Translate, { TranslationContext } from '../../../Translate/Translate.js';
import DocumentContextMenuLink from '../../styledComponents/DocumentContextMenuLink.js';
import DocumentContextMenuSubtitle from '../../styledComponents/DocumentContextMenuSubtitle.js';
import DocumentContextMenuTitle from '../../styledComponents/DocumentContextMenuTitle.js';
import DocumentContextMenuTitleWrapper from '../../styledComponents/DocumentContextMenuTitleWrapper.js';

import {
	IDocumentContextMenuConfiguration,
	IDocumentContextMenuItem,
	IDocumentContextMenuProps,
	IDocumentContextMenuState,
} from './models/index.js';
import {
	DOCUMENT_CONTEXT_MENU_CATEGORY_KEY,
	DOCUMENT_CONTEXT_MENU_COMPONENT_KEY,
	DOCUMENT_CONTEXT_MENU_PARENT_COMPONENT_KEY,
} from './DocumentContextMenu.meta.js';

export class DocumentContextMenu extends ComponentBase<
	IDocumentContextMenuProps,
	IDocumentContextMenuState,
	IDocumentContextMenuConfiguration
> {
	categoryKey = DOCUMENT_CONTEXT_MENU_CATEGORY_KEY;
	componentKey = DOCUMENT_CONTEXT_MENU_COMPONENT_KEY;
	analyticsHelper: common.helpers.AnalyticsHelper<IAppContext> | undefined;
	translationContext: ITranslationContextValue | undefined;

	constructor(props: IDocumentContextMenuProps) {
		super(props);

		makeObservable(this, {});
	}

	get parentComponentKey(): string | undefined {
		return DOCUMENT_CONTEXT_MENU_PARENT_COMPONENT_KEY;
	}

	translate(ariaLabelText: string, placeholders: Record<string, any> = {}) {
		if (this.translationContext) {
			return this.translationContext.translate(ariaLabelText, placeholders);
		}
		return common.commonUtils.replacePlaceholders({ values: placeholders, text: ariaLabelText });
	}

	get ariaLabelPrefix(): string {
		const { ariaLabelPrefix } = this.props;
		if (ariaLabelPrefix) {
			return `${this.translate(ariaLabelPrefix)}, `;
		}
		return '';
	}

	get documents(): IDocumentContextMenuItem[] {
		const { getDocumentsCallback, data: dataConfig, payload: rowData, itemTemplate } = this.props;
		if(getDocumentsCallback) {
			return getDocumentsCallback(rowData, dataConfig, itemTemplate);
		}
		return [];
	}

	render() {
		const { describedById, itemIconProps, label, labelIconProps, iconPosition = 'after' } = this.props;
		return (
			<TranslationContext.Consumer>
				{(translationContext) => {
					this.translationContext = translationContext;
					return this.documents.length > 0 ? 
					(
						<ContextMenu label={label} labelIcon={labelIconProps} describedById={describedById}>
								{this.documents.map((document: IDocumentContextMenuItem, index: number) => {
									const { title, subTitle, href, linkTarget = '_blank' } = document;
									const documentTitle = (
										<DocumentContextMenuTitle
											title={this.translate(`{downloadControlsPrefix} download controls title`, {
												downloadControlsPrefix: `${this.ariaLabelPrefix}${title}`,
											})}
										>
											<Translate>{title}</Translate>
										</DocumentContextMenuTitle>
									);
									const documentSubtitle = (
										<DocumentContextMenuSubtitle
											title={this.translate(`{downloadControlsPrefix} download controls subtitle`, {
												downloadControlsPrefix: `${this.ariaLabelPrefix}${title}`,
											})}
										>
											<Translate>{subTitle}</Translate>
										</DocumentContextMenuSubtitle>
									);
									const icon = itemIconProps && <Icon {...itemIconProps} />;
									return (
										<DocumentContextMenuLink
											key={`document_item_${index}`}
											href={href}
											target={linkTarget}
										>
											{iconPosition === 'before' && icon}
											<DocumentContextMenuTitleWrapper>
												{documentTitle}
												{documentSubtitle}
											</DocumentContextMenuTitleWrapper>
											{iconPosition === 'after' && icon}
										</DocumentContextMenuLink>
									);
								})}
						</ContextMenu>
					)
					: 
						"-"
				}}
			</TranslationContext.Consumer>
		);
	}
}

const StyledDocumentContextMenu = InjectedStyledComponent(
	observer(DocumentContextMenu),
	DOCUMENT_CONTEXT_MENU_CATEGORY_KEY,
	DOCUMENT_CONTEXT_MENU_PARENT_COMPONENT_KEY,
	[DOCUMENT_CONTEXT_MENU_COMPONENT_KEY],
);
export default StyledDocumentContextMenu;
